import { MediaFieldProps } from './Types'
import { FieldArray, useField } from 'formik'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ModalTrigger } from 'almanac-ui/Modal/ModalTrigger'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { Button } from 'blaze-ui/Button'
import { Trash2, Upload5 } from 'blaze-ui/icons'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import React, { PropsWithChildren } from 'react'
import SquareGridStyles from 'screens/BrandKits/components/VMMedia/SquareImageGrid.module.scss'
import RegenerateCampaignsStyles from './RegenerateCampaigns.module.scss'
import { NewImageButton } from 'screens/Campaign/CampaignMedia/NewImageButton'
import { useId } from '@react-aria/utils'
import Styles from './MediaField.module.scss'
import { useHover } from '@react-aria/interactions'
import cx from 'classnames'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

interface NewMediaTriggerProps {
  topic: string
  onUse: (images: ImageMenuSelectedImage[]) => void
}

const NewMediaTrigger = ({ children, topic, onUse }: PropsWithChildren<NewMediaTriggerProps>) => (
  <ModalTrigger
    initialQuery={topic}
    options={{ newStack: true }}
    modal={ImageMenuModal}
    uploadMedia={true}
    title="Add Images & Videos"
    accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
    maxCount={Infinity}
    showSearchBar
    hiddenProviders={['brandKits', 'gifs', 'blazeRecommendations']}
    onUse={onUse}
  >
    {children}
  </ModalTrigger>
)

const Image = ({ file, onRemove }: { file: ImageMenuSelectedImage; onRemove: () => void }) => {
  const { hoverProps, isHovered } = useHover({})
  return (
    <div className={cx(SquareGridStyles.cell, { [Styles.hovered]: isHovered })} {...hoverProps}>
      <img src={file.url} alt={file.file?.name} />
      <div className={Styles.removeButton}>
        <IconButton onPress={onRemove} icon={Trash2} variant="ghost-tertiary" color="var(--light-80)" />
      </div>
    </div>
  )
}

export const MediaField = ({ name, topic }: MediaFieldProps) => {
  const [field] = useField<ImageMenuSelectedImage[]>(name)
  const id = useId()

  return (
    <div role="group" aria-labelledby={id} className={Styles.wrapper}>
      <div className={RegenerateCampaignsStyles.fieldHeading}>
        <Heading level={4} id={id}>
          Add media to use in content
        </Heading>
        <Text variant="secondary" color="var(--dark-40)">
          We will use your images or videos directly
        </Text>
      </div>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            {field.value.length < 1 && (
              <div>
                <NewMediaTrigger topic={topic} onUse={(images: ImageMenuSelectedImage[]) => images.forEach(push)}>
                  <Button frontIcon={Upload5} variant="ghost-tertiary" size="sm" aria-label="Upload Assets">
                    Upload Assets
                  </Button>
                </NewMediaTrigger>
              </div>
            )}
            {field.value.length > 0 && (
              <div className={SquareGridStyles.grid}>
                <NewMediaTrigger topic={topic} onUse={(images: ImageMenuSelectedImage[]) => images.forEach(push)}>
                  <NewImageButton />
                </NewMediaTrigger>
                {field.value.map((file, index) => (
                  <Image key={file.url} file={file} onRemove={() => remove(index)} />
                ))}
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  )
}
